/**
 * Utility functions for Base64 encoding/decoding of HTML content.
 *
 * Purpose: Azure WAF blocks HTTP requests whose JSON body contains raw HTML
 * (angle brackets, script-like patterns, etc.).  Kendo Editor fields produce
 * rich-text HTML that triggers these rules.
 *
 * Strategy:
 *   • Client encodes HTML → Base64 before sending to the API.
 *   • Server decodes Base64 → HTML before persisting to the database.
 *   • The database always stores plain, human-readable HTML.
 *   • GET responses return raw HTML (WAF does not inspect responses).
 */

/**
 * Encode a string (typically HTML) to a Base64 representation that is safe
 * to transmit through Azure WAF.
 *
 * Returns the original value unchanged when it is null, undefined, or empty.
 */
export function encodeHtmlForTransit(value: string | null | undefined): string | null | undefined {
  if (value == null || value === '') {
    return value;
  }
  // encodeURIComponent handles full Unicode → percent-encoded bytes
  // unescape converts percent-encoded bytes back to raw byte chars
  // btoa then base64-encodes those raw byte chars
  return btoa(unescape(encodeURIComponent(value)));
}

/**
 * Decode a Base64-encoded string back to the original HTML.
 *
 * If the value is not valid Base64 it is returned as-is, providing
 * backward compatibility with data that was saved before encoding
 * was introduced.
 */
export function decodeHtmlFromTransit(value: string | null | undefined): string | null | undefined {
  if (value == null || value === '') {
    return value;
  }
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    // Not valid Base64 – return as-is (backward compat)
    return value;
  }
}
